import {
  Checkbox,
  Grid,
  GridColumn,
  GridRow,
  ModalBottomButtons,
  Select,
  SelectOptionType,
  ValueField,
} from '@bp/ui-components';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import classNames from 'classnames';
import { FieldArray, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router-dom';
import { SingleValue } from 'react-select';
import * as yup from 'yup';
import { useConfirm } from '../../../hooks/useConfirm';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  SubstitutionColumns,
  SubstitutionPrintColumnConfigInput,
  useSaveSubstitutionPrintConfigMutation,
  useSubstitutionPrintConfigQuery,
} from '../../../types/planung-graphql-client-defs';
import { showErrorToast, showSuccessUpdateToast } from '../../../utils/toast';
import { Slider } from '../../Slider/Slider';
import styles from './SubstitutionPlanForm.module.scss';
import { SubstitutionPlanFormRow } from './SubstitutionPlanFormRow';

type Orientations = 'landscape' | 'portait';

export type SubstitutionPlanColumn = {
  id: string;
  column: SubstitutionColumns;
  name: string;
  order: number;
  print: boolean;
  width: number | null;
  sortOrder: 1 | 2 | 3 | null;
  ascending: boolean | null;
};

export type SubstitutionPlanFormType = {
  uuid: string;
  showMissingTeachers: boolean;
  showMissingTeachersShortName: boolean;
  showAnnouncements: boolean;
  showSchoolEvents: boolean;
  showTeachersShort: boolean;
  pageOrientation: Orientations;
  printImages: boolean;
  useLessonName: boolean;
  lineScale: number;
  fontScale: number;
  columns: SubstitutionPlanColumn[];
};

type SubstitutionPlanFormProps = {
  readonly?: boolean;
  onLoad?: (isLoading: boolean) => void;
  onClose?: () => void;
};

export const SubstitutionPlanForm = ({ readonly, onLoad, onClose }: SubstitutionPlanFormProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('SUBSTITUTION_PLAN_CONFIG');

  const { confirm, ConfirmationDialog } = useConfirm();

  const [, save] = useSaveSubstitutionPrintConfigMutation();

  const [{ data: printConfigData }] = useSubstitutionPrintConfigQuery({ context });

  const printConfig = printConfigData?.substitutionPrintConfig;

  const initialColumns: SubstitutionPlanColumn[] =
    printConfig && printConfig.columnConfigs
      ? [...printConfig.columnConfigs]
          .sort((a, b) => a.position - b.position)
          .map((config, index) => {
            return {
              id: `row-${index}`,
              column: config.column,
              name: getColumnTranslation(config.column),
              order: config.position,
              print: config.print,
              width: config.width,
              sortOrder: config.sortOrder as 1 | 2 | 3 | null,
              ascending: config.sortDirection === 'ascending',
            };
          })
      : Object.values(SubstitutionColumns).map((column, index) => {
          return {
            id: `row-${index}`,
            column,
            name: getColumnTranslation(column),
            order: index + 1,
            print: true,
            width: 100 / Object.values(SubstitutionColumns).length,
            sortOrder: null,
            ascending: null,
          };
        });

  const initialValues: SubstitutionPlanFormType = {
    uuid: printConfig?.uuid ?? '',
    showMissingTeachers: printConfig?.showMissingTeachers ?? false,
    showMissingTeachersShortName: printConfig?.showMissingTeachersShortName ?? false,
    showAnnouncements: printConfig?.showAnnouncements ?? false,
    showSchoolEvents: printConfig?.showSchoolEvents ?? false,
    showTeachersShort: printConfig?.useTeacherShortname ?? false,
    pageOrientation: (printConfig?.pageOrientation as Orientations) ?? 'portait',
    printImages: printConfig?.printImages ?? false,
    useLessonName: printConfig?.useLessonName ?? false,
    lineScale: printConfig?.lineScale ?? 1.0,
    fontScale: printConfig?.fontScale ?? 1.0,
    columns: initialColumns,
  };

  const orientationOptions: SelectOptionType[] = [
    {
      label: t('substitutions.settings.portait'),
      value: 'portait',
    },
    {
      label: t('substitutions.settings.landscape'),
      value: 'landscape',
    },
  ];

  function getColumnTranslation(name: SubstitutionColumns): string {
    switch (name) {
      case SubstitutionColumns.Class:
        return t('classes.title', { count: 2 });
      case SubstitutionColumns.Comment:
        return t('common.comment');
      case SubstitutionColumns.Lesson:
        return t('common.hour', { count: 1 });
      case SubstitutionColumns.Missing:
        return t('substitutions.missing');
      case SubstitutionColumns.Original:
        return t('substitutions.settings.original');
      case SubstitutionColumns.Room:
        return t('rooms.title', { count: 2 });
      case SubstitutionColumns.Subject:
        return t('subject.title', { count: 2 });
      case SubstitutionColumns.Teacher:
        return t('persons.title', { count: 2 });
      case SubstitutionColumns.Reason:
        return t('substitutions.action');
      default:
        return '';
    }
  }

  async function handleSubmit(values: SubstitutionPlanFormType, formHelpers: FormikHelpers<SubstitutionPlanFormType>) {
    let hasError: boolean = false;

    values.columns.forEach((column, index) => {
      if (isNaN(Number(column.width))) {
        formHelpers.setFieldError(`columns[${index}].width`, 'error');
        hasError = true;
      }
    });

    if (hasError) return;

    const printColumns = values.columns.filter((c) => c.print);
    const widthSum = printColumns.reduce((prev, col) => prev + (col.width ?? 0), 0);

    if (printColumns.length > 0 && Math.round((widthSum + Number.EPSILON) * 100) / 100 !== 100) {
      const confirmed = await confirm({
        title: t('common.hint'),
        message: t('substitutions.settings.widthHint'),
      });
      if (!confirmed) {
        return;
      }
    }

    if (printColumns.length === 0) {
      const confirmed = await confirm({
        title: t('common.hint'),
        message: t('substitutions.settings.noPrintColumnsHint'),
      });
      if (!confirmed) {
        return;
      }
    }

    onLoad && onLoad(true);

    const config = {
      showMissingTeachers: values.showMissingTeachers,
      showMissingTeachersShortName: values.showMissingTeachersShortName,
      showAnnouncements: values.showAnnouncements,
      showSchoolEvents: values.showSchoolEvents,
      useTeacherShortname: values.showTeachersShort,
      pageOrientation: values.pageOrientation,
      printImages: values.printImages,
      useLessonName: values.useLessonName,
      lineScale: values.lineScale,
      fontScale: values.fontScale,
    };

    const columns: SubstitutionPrintColumnConfigInput[] = values.columns.map((column) => {
      return {
        column: column.column,
        position: column.order,
        print: column.print,
        width: column.width ?? 100 / printColumns.length,
        sortOrder: column.sortOrder ?? 0,
        sortDirection: column.ascending ? 'ascending' : 'descending',
      };
    });

    // Das Backend legt die Einstellungen an oder ersetzt sie samt allen Spalten.
    const result = await save({ input: { ...config, columnConfigs: columns } }, context);

    if (result.error) {
      showErrorToast(result.error);
    } else {
      showSuccessUpdateToast();
    }

    onLoad && onLoad(false);
    formHelpers.resetForm();
    onClose && onClose();
  }

  function handleDragEnd(values: SubstitutionPlanFormType, event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const activeColumn = values.columns.find((i) => i.id === active.id);
      const overColumn = values.columns.find((i) => i.id === over?.id);
      if (activeColumn && overColumn) {
        const oldIndex = values.columns.indexOf(activeColumn);
        const newIndex = values.columns.indexOf(overColumn);
        const newColumns = arrayMove(values.columns, oldIndex, newIndex);
        values.columns = newColumns.map((c, index) => {
          return {
            ...c,
            id: `row-${index}`,
            order: index + 1,
          };
        });
      }
    }
  }

  return (
    <>
      <Formik<SubstitutionPlanFormType> initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        {({ setFieldValue, values, dirty, resetForm, errors, submitForm, handleChange, setTouched, touched }) => {
          async function calculateWidth() {
            const printColumns = values.columns.filter((c) => c.print);
            const width = 100 / printColumns.length;
            for (const column of printColumns) {
              await setFieldValue(`columns.${column.order - 1}.width`, width);
            }
          }

          return (
            <Form className={styles['substitution-plan-form']}>
              <Grid>
                <GridRow>
                  <GridColumn width={3}>
                    <ValueField title={t('substitutions.missingTeachers')} mode='headline' position='top'>
                      <Checkbox
                        name={readonly ? 'show-missing-teachers-readonly' : 'show-missing-teachers'}
                        checked={values.showMissingTeachers}
                        onChange={async (e) => {
                          await setFieldValue('showMissingTeachers', e.target.checked);
                        }}
                        label={t('common.show')}
                        readonly={readonly}
                      />
                      <Checkbox
                        name={
                          readonly ? 'show-missing-teachers-shortnames-readonly' : 'show-missing-teachers-shortnames'
                        }
                        checked={values.showMissingTeachersShortName}
                        onChange={async (e) => {
                          await setFieldValue('showMissingTeachersShortName', e.target.checked);
                        }}
                        label={t('substitutions.settings.showShortnames')}
                        readonly={readonly}
                      />
                    </ValueField>
                  </GridColumn>
                  <GridColumn width={3}>
                    <ValueField title={t('announcements.title', { count: 2 })} mode='headline' position='top'>
                      <Checkbox
                        name={readonly ? 'show-announcements-readonly' : 'show-announcements'}
                        checked={values.showAnnouncements}
                        onChange={async (e) => {
                          await setFieldValue('showAnnouncements', e.target.checked);
                        }}
                        label={t('common.show')}
                        readonly={readonly}
                      />
                    </ValueField>
                  </GridColumn>
                  <GridColumn width={3}>
                    <ValueField
                      title={t('schoolEvents.title', {
                        context: 'short',
                        count: 2,
                      })}
                      mode='headline'
                      position='top'
                    >
                      <Checkbox
                        name={readonly ? 'show-school-events-readonly' : 'show-school-events'}
                        checked={values.showSchoolEvents}
                        onChange={async (e) => {
                          await setFieldValue('showSchoolEvents', e.target.checked);
                        }}
                        label={t('common.show')}
                        readonly={readonly}
                      />
                    </ValueField>
                  </GridColumn>
                  <GridColumn width={3}>
                    <ValueField title={t('persons.title', { count: 2 })} mode='headline' position='top'>
                      <Checkbox
                        name={readonly ? 'show-teachers-shortnames-readonly' : 'show-teachers-shortnames'}
                        checked={values.showTeachersShort}
                        onChange={async (e) => {
                          await setFieldValue('showTeachersShort', e.target.checked);
                        }}
                        label={t('substitutions.settings.showShortnames')}
                        readonly={readonly}
                      />
                    </ValueField>
                  </GridColumn>
                  <GridColumn width={3}>
                    <ValueField title={t('substitutions.settings.lineHeight')} mode='headline' position='top'>
                      <div className={styles.sliderContainer}>
                        <Slider
                          value={[values.lineScale]}
                          onValueChange={(value) => setFieldValue('lineScale', value[0])}
                          min={0.75}
                          max={1.5}
                          step={0.25}
                          disabled={readonly}
                        />
                        <div className={styles.sliderLabel}>
                          {values.lineScale === 0.75 && t('substitutions.settings.small')}
                          {values.lineScale === 1.0 && t('substitutions.settings.normal')}
                          {values.lineScale === 1.25 && t('substitutions.settings.large')}
                          {values.lineScale === 1.5 && t('substitutions.settings.veryLarge')}
                        </div>
                      </div>
                    </ValueField>
                  </GridColumn>

                  <GridColumn width={3}>
                    <ValueField title={t('substitutions.settings.fontSize')} mode='headline' position='top'>
                      <div className={styles.sliderContainer}>
                        <Slider
                          value={[values.fontScale]}
                          onValueChange={(value) => setFieldValue('fontScale', value[0])}
                          min={0.75}
                          max={1.5}
                          step={0.25}
                          disabled={readonly}
                        />
                        <div className={styles.sliderLabel}>
                          {values.fontScale === 0.75 && t('substitutions.settings.small')}
                          {values.fontScale === 1.0 && t('substitutions.settings.normal')}
                          {values.fontScale === 1.25 && t('substitutions.settings.large')}
                          {values.fontScale === 1.5 && t('substitutions.settings.veryLarge')}
                        </div>
                      </div>
                    </ValueField>
                  </GridColumn>
                </GridRow>

                <GridRow headline={t('substitutions.settings.columnsOrder')} className={styles['columns-order']}>
                  <div
                    className={classNames(styles.header, {
                      [styles.readonly]: readonly,
                    })}
                  >
                    <div className={styles.index}></div>
                    <div className={styles.name}>{t('common.name')}</div>
                    <div className={styles.print}>{t('common.print')}</div>
                    <div className={styles.width}>{t('substitutions.settings.widthPercent')}</div>
                    <div className={styles.order}>{t('substitutions.settings.sortOrder')}</div>
                    <div className={styles.direction}>{t('substitutions.settings.sortDirection')}</div>
                  </div>

                  <DndContext
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    collisionDetection={closestCenter}
                    onDragEnd={async (e) => {
                      handleDragEnd(values, e);
                      await setTouched({ columns: [{ order: true }] });
                    }}
                  >
                    <SortableContext strategy={verticalListSortingStrategy} items={values.columns}>
                      <FieldArray
                        name={readonly ? 'columns-readonly' : 'columns'}
                        render={(arrayHelpers) => {
                          return values.columns.map(({ column }, index) => (
                            <SubstitutionPlanFormRow
                              key={column}
                              index={index}
                              helper={arrayHelpers}
                              readonly={!!readonly}
                              onChange={handleChange}
                            />
                          ));
                        }}
                      />
                    </SortableContext>
                  </DndContext>
                </GridRow>

                <GridRow headline={t('substitutions.settings.print')} spacingTop='m' spacingBottom='none'>
                  <GridColumn width={3}>
                    <ValueField
                      title={t('substitutions.settings.orientation')}
                      mode='headline'
                      position='top'
                      className={styles.select}
                    >
                      <Select
                        options={orientationOptions}
                        value={orientationOptions.find((o) => o.value === values.pageOrientation)}
                        onChange={async (option) => {
                          const opt = option as SingleValue<SelectOptionType>;
                          await setFieldValue('pageOrientation', opt?.value);
                        }}
                        dense
                        name={readonly ? 'orientation-readonly' : 'orientation'}
                        menuPosition='fixed'
                        readonly={readonly}
                        className='mt-2'
                      />
                    </ValueField>
                  </GridColumn>
                  <GridColumn width={3}>
                    <ValueField title={t('substitutions.settings.content')} mode='headline' position='top'>
                      <Checkbox
                        name={readonly ? 'show-images-readonly' : 'show-images'}
                        checked={values.printImages}
                        onChange={async (e) => {
                          await setFieldValue('printImages', e.target.checked);
                        }}
                        label={t('substitutions.settings.showImages')}
                        readonly={readonly}
                      />
                      <Checkbox
                        name={readonly ? 'use-lesson-name-readonly' : 'use-lesson-name'}
                        checked={values.useLessonName}
                        onChange={async (e) => {
                          await setFieldValue('useLessonName', e.target.checked);
                        }}
                        label={t('substitutions.settings.useLessonName')}
                        readonly={readonly}
                      />
                    </ValueField>
                  </GridColumn>
                </GridRow>
              </Grid>

              {!readonly && (
                <ModalBottomButtons
                  additionalButtons={[
                    {
                      hierarchy: 'tertiary',
                      text: t('substitutions.settings.calculateWidth'),
                      callback: calculateWidth,
                    },
                  ]}
                  closeButton={{
                    callback: () => {
                      onClose && onClose();
                      resetForm();
                    },
                  }}
                  submitButton={{
                    callback: () => submitForm(),
                    disabled: !dirty && Object.keys(touched).length === 0,
                  }}
                  errors={errors}
                />
              )}
            </Form>
          );
        }}
      </Formik>

      <ConfirmationDialog />
    </>
  );
};
